/**
 * ActionOverflow — universal "More ▾" dropdown for record-page and section
 * headers (Push #28 rule; cap tightened Push 4-1af 2026-07-10).
 *
 * Rule: any record-page or section header shows AT MOST 2 inline action
 * buttons (per Ean Push 4-1af — was 3). The 3rd + remaining collapse into
 * this overflow menu so the header stays scannable.
 *
 * Action priority order (decided by the caller, not this component):
 *   1. Primary action (filled teal) — always inline, always rightmost
 *   2. Secondary action #1 (outlined) — inline if room
 *   3+. Everything else → overflow
 *
 * Back-nav links ("← Connectors") are a separate concern and don't count
 * against the 3-action limit. They render at the far-left of the header
 * strip, not in the action group.
 *
 * Usage (RecordActions and ButtonCluster build these items for you):
 *   <ActionOverflow items={[
 *     { label: 'Copy prompt', onClick: handleCopy },
 *     { label: 'Export JSON', onClick: handleExport },
 *     { label: 'Delete', onClick: handleDelete, danger: true },
 *   ]} />
 *
 * PLAT-329 (PLAT-168 Menus 2c): the menu is Foundation's Menu, in the pattern
 * of ListRow's row menu (PLAT-249) and LightningTabs' overflow (PLAT-324). It
 * is not modal, so the page stays scrollable while it's open and an item can
 * put focus back on the trigger before its action runs (the menu-button
 * pattern, PLAT-248): a dialog the action opens returns focus there. Radix
 * gives it arrow-key movement, typeahead, Escape and outside-click closing.
 */

import { Button, Menu, MenuContent, MenuItem, MenuTrigger } from '@olytic/foundation';
import { type ReactNode, useRef, useState } from 'react';

export interface ActionOverflowItem {
  label: string;
  onClick: () => void;
  /** Optional leading element (icon, etc.). Skipped per Push #16 icon
   *  discipline unless icons are already in the surrounding visual lang. */
  icon?: ReactNode;
  /** Destructive ops render in danger color. */
  danger?: boolean;
  /** Greyed out + non-clickable. Tooltip explains why via `title`. */
  disabled?: boolean;
  /** Aria/native title for hover hint. */
  title?: string;
}

interface ActionOverflowProps {
  items: ActionOverflowItem[];
  /** Override the trigger's accessible name. Default "More" names it "More actions". */
  triggerLabel?: string;
  /** Trigger button size matches surrounding action buttons. */
  size?: 'sm' | 'md';
}

export function ActionOverflow({ items, triggerLabel = 'More', size = 'md' }: ActionOverflowProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);

  if (items.length === 0) return null;

  const name = triggerLabel === 'More' ? 'More actions' : triggerLabel;

  return (
    <Menu modal={false} onOpenChange={setOpen}>
      {/* Push 4-1t (2026-07-09): icon-only chevron trigger per Ean nitpick —
         "More" text was redundant when a caret already conveys expand.
         MenuTrigger gives it aria-haspopup="menu" and aria-expanded. */}
      <MenuTrigger asChild>
        <Button
          ref={trigger}
          variant="outline"
          size={size === 'sm' ? 'sm' : 'md'}
          aria-label={name}
          title={name}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            style={{
              transition: 'transform 120ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </MenuTrigger>
      <MenuContent align="end">
        {items.map((item, i) => (
          <MenuItem
            key={i}
            destructive={item.danger === true}
            disabled={item.disabled === true}
            title={item.title}
            onSelect={() => {
              trigger.current?.focus();
              item.onClick();
            }}
          >
            {item.icon !== undefined && <span aria-hidden="true">{item.icon}</span>}
            <span>{item.label}</span>
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
